/* eslint-disable no-console */
import { types, flow, getRoot, applySnapshot, getSnapshot } from "mobx-state-tree";

import { Aggregation } from "./AggregationStore";
import { ApiStore } from "./ApiStore";
import { Room } from "./RoomStore";
import { Sensor } from "./SensorStore";

const SensorGroup = types.compose(
  ApiStore,
  types
    .model({
      id: types.identifier,
      human_name: types.optional(types.string, ""),
      sensors: types.array(types.late(() => types.reference(Sensor))),
      rooms: types.array(types.late(() => types.reference(Room))),
      readings: types.map(types.late(() => Aggregation)),
    })
    .actions(self => ({
      pullReadings: flow(function *pullReadings() {
        const notifications = getRoot(self).notifications();

        try {
          const res = yield self.fetch2(`/sensor_groups/${self.id}/readings/`);
          const json = yield res.json();

          self.readings.clear();
          json.forEach(r => self.readings.put(r));

          yield Promise.resolve();
        } catch (err) {
          console.error("Failed to fetch and update sensor group readings.\n", err);
          notifications.error("Unknown error occured.");
        }
      }),
      pullSensors: flow(function *pullSensors() {
        const notifications = getRoot(self).notifications();

        try {
          const res = yield self.fetch2(`/sensor_groups/${self.id}/sensors/`);
          const json = yield res.json();

          getRoot(self).sensorStore.update(json);

          self.sensors.clear();
          json.forEach(s => {
            self.sensors.push(s.id);
          });

          yield Promise.resolve();
        } catch (err) {
          console.error("Failed to fetch and update sensors.\n", err);
          notifications.error("Unknown error occured.");
        }
      }),
      updateReadings: flow(function* updateReadings(readingsJson) {
        self.readings = readingsJson;
        yield Promise.resolve();
      }),
      updateSensors: flow(function* updateSensors(sensorsJson) {
        // FIXME: This should update the sensors themselves with any updated
        // info in addition to just updating the IDs.
        self.sensors = sensorsJson.map(s => s.id);
        yield Promise.resolve();
      }),
      fetchAndUpdateReadings: flow(function* fetchAndUpdateReadings() {
        yield self.fetch(
          `/sensor_groups/${self.id}/readings/`,
          {},
          self.updateReadings,
          err =>
            console.error(
              "Failed to fetch and update sensor group readings.\n",
              err
            )
        );
      }),
      addSensorsByIds: flow(function* addSensorsByIds(sensorIds) {
        yield self.fetch(
          `/sensor_groups/${self.id}/sensors/`,
          {
            method: "POST",
            body: JSON.stringify(sensorIds)
          },
          self.updateSensors,
          err => console.error("Failed to add sensors to sensor group.\n", err)
        );
      }),
      removeSensorsByIds: flow(function* removeSensorsByIds(sensorIds) {
        yield self.fetch(
          `/sensor_groups/${self.id}/sensors/`,
          {
            method: "DELETE",
            body: JSON.stringify(sensorIds)
          },
          self.updateSensors,
          err =>
            console.error("Failed to delete sensors from sensor group.\n", err)
        );
      })
    }))
    .views(self => ({
      orderedReadings() {
        const readingKeys = Array.from(self.readings.keys());
        const readings = readingKeys
                .map(k => getSnapshot(self.readings.get(k)))
                .sort((a, b) => a.timestamp < b.timestamp);
        return readings;
      },
    }))
);

const SensorGroupStore = types.compose(
  ApiStore,
  types
    .model({
      sensor_groups: types.map(SensorGroup)
    })
    .actions(self => ({
      update: flow(function* update(sensorGroupsJson) {
        sensorGroupsJson.forEach(sensorGroupJson => {
          self.sensor_groups.put(sensorGroupJson);
        });
        yield Promise.resolve();
      }),
      fetchAndUpdate: flow(function* fetchAndUpdate() {
        yield self.fetch("/sensor_groups/", {}, self.update, err =>
          console.error("Failed to fetch and update sensor groups.\n", err)
        );
      }),
      reset: flow(function* reset() {
        applySnapshot(self, {});
        yield Promise.resolve();
      }),
    }))
);

export { SensorGroup, SensorGroupStore };
